/**
 * Free Results — Professional Diagnostic Report CTA.
 *
 * Stronger upgrade card with explicit RM 499 pricing and the three contact
 * paths the user requested (Request Professional Report / WhatsApp / Email).
 * The "Request Professional Report" button opens WhatsApp with the
 * pre-populated assessment context — same backend behaviour as the existing
 * UnlockProReportCard, but visually distinct for the free results page.
 */
import { Button } from '@/components/ui/button';
import {
  FileText,
  WhatsappLogo,
  Envelope,
  CheckCircle,
} from '@phosphor-icons/react';
import {
  CONTACT,
  buildWhatsAppConsultationUrl,
  buildEmailConsultationHref,
} from '@/lib/constants';

const NAVY = '#0B2951';
const TEAL = '#008C85';

const PRO_HIGHLIGHTS = [
  'Domain-by-domain consulting analysis',
  'Benchmark position vs. SME / Mature SME / Best Practice',
  'AI Opportunity Matrix with technology recommendations',
  'Management Priority Matrix',
  'SMART-Workflow transformation roadmap',
  '30 to 90-day management action plan',
];

const consultationContextFrom = (assessment, assessmentId) => ({
  organization: assessment?.respondent?.organization_name,
  contactName: assessment?.respondent?.name,
  assessmentRef: assessmentId,
  awsaiScore: assessment?.overall_score,
  requestedService: 'Professional Diagnostic Report',
});

const ProfessionalReportCTA = ({ assessment, assessmentId }) => {
  const ctx = consultationContextFrom(assessment, assessmentId);
  const whatsappHref = buildWhatsAppConsultationUrl(ctx);
  const emailHref = buildEmailConsultationHref(ctx);
  return (
    <section
      className="rounded-2xl p-6 sm:p-10 mb-8 shadow-xl text-white"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #103A6D 60%, ${TEAL} 100%)` }}
      data-testid="professional-report-cta"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
          <FileText size={20} weight="bold" className="text-amber-300" />
        </div>
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-amber-300">
          Professional Diagnostic Report
        </div>
      </div>
      <h3 className="text-2xl sm:text-3xl font-bold mb-3 leading-tight">
        Need the full management report?
      </h3>
      <p className="text-sm sm:text-base text-slate-100/90 max-w-2xl mb-5 leading-relaxed">
        The AWSAI Professional Diagnostic Report provides domain-by-domain analysis, benchmark
        position, AI opportunity matrix, management priority matrix, SMART-Workflow roadmap, and a
        30 to 90-day action plan.
      </p>

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6 max-w-3xl">
        {PRO_HIGHLIGHTS.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-slate-100">
            <CheckCircle size={18} weight="fill" className="text-teal-300 flex-shrink-0 mt-0.5" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <div
        className="bg-white/10 border border-white/20 rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6"
        data-testid="pro-pricing-block"
      >
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-1">
            Investment
          </div>
          <div className="text-2xl sm:text-3xl font-bold">
            Professional Diagnostic Report: <span className="text-amber-300">RM 499</span>
          </div>
        </div>
        <div className="text-xs text-slate-200 max-w-sm leading-relaxed">
          Delivered after a short consultation with Aurea Digital Solutions. Assessment reference
          and AWSAI score are pre-filled in your request.
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          asChild
          size="lg"
          className="bg-amber-300 hover:bg-amber-200 text-slate-900 font-semibold flex-1 sm:flex-initial"
        >
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-request-professional-report"
          >
            <FileText size={20} weight="bold" className="mr-2" />
            Request Professional Report
          </a>
        </Button>
        <Button
          asChild
          size="lg"
          className="bg-green-600 hover:bg-green-700 text-white font-semibold flex-1 sm:flex-initial"
        >
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-whatsapp"
          >
            <WhatsappLogo size={20} weight="fill" className="mr-2" />
            Enquire via WhatsApp
          </a>
        </Button>
        <Button
          asChild
          size="lg"
          variant="outline"
          className="border-2 border-white/40 text-white hover:bg-white/10 flex-1 sm:flex-initial"
        >
          <a href={emailHref} data-testid="cta-email">
            <Envelope size={20} weight="bold" className="mr-2" />
            Email Aurea Digital Solutions
          </a>
        </Button>
      </div>

      <div className="mt-5 text-xs text-slate-200/80 leading-relaxed">
        WhatsApp <span className="font-medium text-white">{CONTACT.WHATSAPP_NUMBER}</span>
        {' '}· Email <span className="font-medium text-white">{CONTACT.EMAIL}</span>
      </div>
    </section>
  );
};

export default ProfessionalReportCTA;
